import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  Database,
  Users,
  FileText,
  Signature
} from "lucide-react";
import { toast } from "sonner";
import UserSelect from "features/user-select/user-select";
import { mergeInCompany } from "services/user-select.service";
import {
  SECONDARY_BUTTON,
  SettingsCard,
  StatusBanner,
} from "containers/settings/ui";
import UserMergeTabs from "./UserMergeTabs";

type MergeStatus = 'idle' | 'analyzing' | 'ready' | 'merging' | 'completed';

const UserMerge = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [sourceUserId, setSourceUserId] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [status, setStatus] = useState<MergeStatus>('idle');
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [resultData, setResultData] = useState<any>(null);
  const [analysisRetryKey, setAnalysisRetryKey] = useState(0);
  const [confirmation, setConfirmation] = useState("");
  const isMergeSubmittingRef = useRef(false);

  // Auto-analyze when both users are selected; abort in-flight requests when the pair or company changes
  useEffect(() => {
    if (isMergeSubmittingRef.current) return;

    const controller = new AbortController();
    const selectionInvalid =
      !sourceUserId || !targetUserId || sourceUserId === targetUserId || !companyId;

    // A word typed for one pair must not confirm the next one.
    setConfirmation("");

    if (selectionInvalid) {
      setAnalysisData(null);
      setStatus("idle");
      return () => controller.abort();
    }

    setStatus("analyzing");

    void (async () => {
      try {
        const data = await mergeInCompany(
          companyId,
          sourceUserId,
          targetUserId,
          true,
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        setAnalysisData(data);
        setStatus("ready");
      } catch (error: unknown) {
        if (controller.signal.aborted) return;
        const code = (error as { code?: string }).code;
        if (code === "ERR_CANCELED") return;
        toast.error(t("error_analyzing_merge"));
        setAnalysisData(null);
        setStatus("idle");
      }
    })();

    return () => controller.abort();
  }, [sourceUserId, targetUserId, companyId, analysisRetryKey, t]);

  const isSelectionValid = Boolean(
    sourceUserId && targetUserId && sourceUserId !== targetUserId
  );
  const sameUserSelected = Boolean(
    sourceUserId && targetUserId && sourceUserId === targetUserId
  );

  const idleGuidanceMessage = sameUserSelected
    ? t("merge_invalid_same_user")
    : !sourceUserId && !targetUserId
      ? t("select_users_to_begin_analysis")
      : t("merge_select_both_users");

  const confirmWord = t("merge_confirm_word");
  const isConfirmed = confirmation.trim().toUpperCase() === confirmWord;

  const handleStartMerge = async () => {
    if (!companyId || !isSelectionValid || !isConfirmed || status === 'merging' || isMergeSubmittingRef.current) return;
    isMergeSubmittingRef.current = true;
    setStatus('merging');
    try {
      const data = await mergeInCompany(companyId, sourceUserId, targetUserId, false);
      setResultData(data);
      setStatus('completed');
      toast.success(t("merge_completed_successfully"));
    } catch (error) {
      toast.error(t("error_performing_merge"));
      setAnalysisData(null);
      setStatus('idle');
      setAnalysisRetryKey((prev) => prev + 1);
    } finally {
      isMergeSubmittingRef.current = false;
    }
  };

  const renderComparisonRow = (rowKey: string, labelKey: string, sourceCount: number, targetCount: number, icon: React.ReactNode) => {
    const total = sourceCount + targetCount;
    return (
      <tr key={rowKey} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="text-gray-400">{icon}</div>
            <span className="font-medium text-gray-700">{t(labelKey) || labelKey}</span>
          </div>
        </td>
        <td className="py-4 px-4 text-center">
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${sourceCount > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
                {sourceCount}
            </span>
        </td>
        <td className="py-4 px-4 text-center text-gray-500">{targetCount}</td>
        <td className="py-4 px-4 text-right">
            <span className="font-bold text-blue-600">{total}</span>
        </td>
      </tr>
    );
  };

  const getTableRows = (summary: any, target: any) => {
    const sourceSummary = summary || {};
    const targetSummary = target || {};
    const keys = Array.from(
      new Set([
        ...Object.keys(sourceSummary),
        ...Object.keys(targetSummary),
      ])
    );
    return keys.map(key => {
        let icon = <Database size={16} />;
        if (key.includes('files')) icon = <FileText size={16} />;
        else if (key.includes('user')) icon = <Users size={16} />;
        else if (key.includes('signature')) icon = <Signature size={16} />;

        return renderComparisonRow(key, key, sourceSummary[key] || 0, targetSummary[key] || 0, icon);
    });
  };

  const mergeDoneView = resultData && (
    <div className="space-y-5">
      <StatusBanner
        tone="ok"
        title={t("merge_successful")}
        detail={
          <>
            {t("all_records_have_been_successfully_migrated")}{" "}
            {t("merge_source_membership_closed")}
          </>
        }
      />
      <p className="text-sm text-gray-600">
        {t("batch_id")}:{" "}
        <span className="font-mono">{resultData.mergeBatchId?.substring(0,8)}...</span>
      </p>
      <button
        type="button"
        onClick={() => navigate(`/app/${companyId}/settings/company`)}
        className={SECONDARY_BUTTON}
      >
        {t("return_to_settings")}
      </button>
    </div>
  );

  const isAnalysisShown = (status === 'ready' || status === 'merging') && analysisData;

  const mergeFormCard = (
    <SettingsCard description={t("merge_intro")}>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div>
          <UserSelect
            value={sourceUserId}
            onChange={(val) => setSourceUserId(val)}
            label="source_user_to_close"
            isRequired
            isDisable={status === 'merging'}
            showUserEmail
          />
          <p className="mt-2 text-xs text-gray-500">{t("all_records_below_will_be_transferred")}</p>
        </div>
        <div>
          <UserSelect
            value={targetUserId}
            onChange={(val) => setTargetUserId(val)}
            label="target_user_survivor"
            isRequired
            isDisable={status === 'merging'}
            showUserEmail
          />
          <p className="mt-2 text-xs text-gray-500">{t("this_user_will_inherit_all_records")}</p>
        </div>
      </div>

      {status === 'analyzing' && (
        <p className="mt-5 flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="animate-spin text-indigo-600" size={16} />
          {t("analyzing_data_structures")}...
        </p>
      )}

      {status === "idle" && !isSelectionValid && (
        <div className="mt-5">
          {sameUserSelected ? (
            <StatusBanner tone="warn" title={idleGuidanceMessage} />
          ) : (
            <p className="text-sm text-gray-600">{idleGuidanceMessage}</p>
          )}
        </div>
      )}

      {isAnalysisShown && (
        <div className="mt-5 space-y-5">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-900">{t("merge_impact_analysis")}</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500">
                    <th className="py-3 px-4 text-left font-medium">{t("data_category")}</th>
                    <th className="py-3 px-4 text-center font-medium">{t("source_transfer")}</th>
                    <th className="py-3 px-4 text-center font-medium">{t("target_existing")}</th>
                    <th className="py-3 px-4 text-right font-medium">{t("final_count")}</th>
                  </tr>
                </thead>
                <tbody>
                  {getTableRows(analysisData.sourceSummary, analysisData.targetSummary)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="max-w-md">
            <label htmlFor="mergeConfirmation" className="mb-1 block text-sm font-medium text-gray-700">
              {t("merge_confirm_label", { word: confirmWord })}
            </label>
            <input
              id="mergeConfirmation"
              type="text"
              autoComplete="off"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 md:text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          <button
            type="button"
            onClick={handleStartMerge}
            disabled={!isConfirmed || status === 'merging'}
            className="inline-flex min-h-11 items-center md:min-h-9 rounded-lg border border-red-700 bg-red-700 px-4 text-sm font-medium text-white hover:bg-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'merging' ? `${t("processing_merge")}...` : t("confirm_and_start_transfer")}
          </button>
        </div>
      )}
    </SettingsCard>
  );

  return (
    <div className="space-y-5">
      <UserMergeTabs active="merge" />
      {status === 'completed' ? mergeDoneView : mergeFormCard}
    </div>
  );
};

export default UserMerge;
